import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SetupAnalysisResult } from '@/types/agent/setup';
import { ReviewSectionCard } from "./ReviewSectionCard";
import { GenieCategories } from "@/types/shared/categories";
import GenieAvatarGallerySelector from "@/components/agent/vision/GenieAvatarGallerySelector";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import BrandingModal from "@/components/branding/BrandingModal";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Agent } from "@/types/agent/agent";
import { useSubscription } from "@/contexts/billing/subscription";
import { useRole } from "@/hooks/auth/useRole";
import { Lock } from "lucide-react";

interface ConfirmationBrandingSectionProps {
  analysis: SetupAnalysisResult;
  onChange: (field: keyof SetupAnalysisResult, value: any) => void;
  exampleGenies: Pick<Agent, "id" | "name" | "branding">[];
  loadingExamples: boolean;
}

export const ConfirmationBrandingSection = ({
  analysis,
  onChange,
  exampleGenies,
  loadingExamples,
}: ConfirmationBrandingSectionProps) => {
  const { isSubscribed } = useSubscription();
  const { isInternalAdmin } = useRole();
  const isBrandingLocked = !isInternalAdmin() && !isSubscribed;

  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [selectedExampleGenie, setSelectedExampleGenie] = useState<any>(null);

  const category = GenieCategories.find((cat) => cat.id === analysis.category) || GenieCategories.find((cat) => cat.id === 'other')!;

  const previewBranding = analysis.branding || {
    ...DEFAULT_BRANDING,
  };

  const handleGenieImageSelect = (imageUrl: string) => {
    onChange("branding", {
      ...analysis.branding,
      customAvatarUrl: imageUrl,
    });
  };

  const handleCustomizeBranding = () => {
    setSelectedExampleGenie(null); // Show user's own branding
    setShowBrandingModal(true);
  };

  const handleExampleClick = (example: any) => {
    setSelectedExampleGenie(example); // Show example genie's branding
    setShowBrandingModal(true);
  };

  const handleBrandingChange = (updatedBranding: any) => {
    onChange("branding", updatedBranding);
  };

  // Create a temporary agent object for the branding modal
  // Use selected example if viewing an example, otherwise use user's genie
  const tempAgent = selectedExampleGenie || {
    id: 'temp',
    name: analysis.genieName,
    branding: previewBranding,
  } as any;

  // Fallback examples if real genies aren't found
  const fallbackExamples = [
    {
      id: "fallback-1",
      name: "Content Lead Genie",
      branding: {
        primaryColor: "#3B82F6",
        secondaryColor: "#8B5CF6",
        gradientEnabled: true,
        customAvatarUrl: "/genie-mascots/role/receptionist.png",
      }
    },
    {
      id: "fallback-2",
      name: "App Dev",
      branding: {
        primaryColor: "#F97316",
        secondaryColor: "#EF4444",
        gradientEnabled: true,
        customAvatarUrl: "/genie-mascots/role/sales.png",
      }
    },
    {
      id: "fallback-3",
      name: "Tesla-genie",
      branding: {
        primaryColor: "#E82127",
        secondaryColor: "#000000",
        gradientEnabled: true,
        customAvatarUrl: "/genie-mascots/role/operations.png",
      }
    }
  ];

  // Use real genies if available, fill with fallbacks to ensure we always have 3
  const brandingExamples = exampleGenies && exampleGenies.length > 0
    ? [...exampleGenies, ...fallbackExamples].slice(0, 3)
    : fallbackExamples;

  return (
    <>
      <ReviewSectionCard
        title="Branding"
        subtitle="Customize your genie's appearance"
        genieIcon="play"
        categoryColor={category.color}
      >
        {/* Two-column layout: 1/3 for avatar, 2/3 for branding examples */}
        <div className="flex flex-col md:grid md:grid-cols-3 md:gap-6">

          {/* Left Column (1/3): Avatar Selection */}
          <div className="md:col-span-1 flex flex-col pb-12 md:pb-0">
            {!isBrandingLocked ? (
              <>
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Preview Avatar
                </h4>
                <p className="text-xs text-gray-500 mb-6 md:mb-4">
                  You can upload your own logo in custom branding
                </p>
              </>
            ) : (
              <>
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Select Your Genie Avatar
                </h4>
                <p className="text-xs text-gray-500 mb-6 md:mb-4">
                  Choose an avatar that represents your brand
                </p>
              </>
            )}
            <div className="flex justify-center md:justify-start">
              <GenieAvatarGallerySelector
                selectedImage={previewBranding.customAvatarUrl}
                onImageSelect={handleGenieImageSelect}
                brandColor={previewBranding.primaryColor || DEFAULT_BRANDING.primaryColor}
                secondaryColor={previewBranding.secondaryColor}
                gradientEnabled={previewBranding.gradientEnabled}
                gradientAngle={previewBranding.gradientAngle}
                size={150}
              />
            </div>
          </div>

          {/* Right Column (2/3): Custom Branding Examples */}
          <div className="md:col-span-2 flex flex-col">
            {!isBrandingLocked ? (
              <>
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Ready to Customize Your Branding
                </h4>
                <p className="text-xs text-gray-500 mb-4">
                  Set up your custom colors, gradients, and logo below
                </p>
              </>
            ) : (
              <>
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Custom Branding Examples
                </h4>
                <p className="text-xs text-gray-500 mb-4">
                  See what's possible with custom colors, gradients, and logos
                </p>
              </>
            )}


            {/* Responsive example avatars grid for mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {loadingExamples ? (
                // Loading skeleton
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-[140px] md:h-[140px] rounded-full bg-gray-200 animate-pulse" />
                  </div>
                ))
              ) : (
                brandingExamples.map((example, index) => {
                  const exampleBranding = example.branding || DEFAULT_BRANDING;
                  return (
                    <button
                      key={example.id || index}
                      onClick={() => handleExampleClick(example)}
                      className="flex flex-col items-center group cursor-pointer transition-transform hover:scale-105"
                    >
                      <div className="relative">
                        <GenieAvatar
                          customAvatarUrl={exampleBranding.logoUrl || exampleBranding.customAvatarUrl}
                          color={exampleBranding.primaryColor || DEFAULT_BRANDING.primaryColor}
                          secondaryColor={exampleBranding.secondaryColor}
                          gradientEnabled={exampleBranding.gradientEnabled}
                          gradientAngle={exampleBranding.gradientAngle || 135}
                          avatarBackgroundOpacity={exampleBranding.avatarBackgroundOpacity}
                          size={140}
                          hasCustomBranding={true}
                          speaking={false}
                          listening={false}
                          isConnecting={false}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 rounded-full flex items-center justify-center transition-all">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
                            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleCustomizeBranding}
              className="w-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white hover:opacity-90"
            >
              {isBrandingLocked ? (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Set Up Your Own Custom Branding
                </>
              ) : (
                "Customize Your Branding"
              )}
            </Button>
          </div>

        </div>
      </ReviewSectionCard>

      {/* Branding Modal */}
      {showBrandingModal && (
        <BrandingModal
          genie={tempAgent}
          isOpen={showBrandingModal}
          onClose={() => setShowBrandingModal(false)}
          isLocked={!selectedExampleGenie && isBrandingLocked}
          isPreview={!!selectedExampleGenie}
          onBrandingChange={!selectedExampleGenie ? handleBrandingChange : undefined}
        />
      )}
    </>
  );
};
